/**
 * SalonID Process Steps - Reversible Animation Controller
 * Handles bidirectional scroll-based animations
 */
(function() {
  'use strict';

  class ProcessStepsAnimator {
    constructor(section) {
      this.section = section;
      this.steps = Array.from(section.querySelectorAll('.salonid-process-steps__step'));
      this.images = Array.from(section.querySelectorAll('[data-step-image]'));
      this.progressFill = section.querySelector('[data-progress-fill]');
      this.progressBar = section.querySelector('.salonid-process-steps__progress-bar');
      
      this.ticking = false;
      this.intersectionObserver = null;
      
      this.init();
    }

    init() {
      this.setupIntersectionObserver();
      this.setupScrollProgress();
      
      // Bind methods
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: show all elements immediately
        this.steps.forEach(step => step.classList.add('animate-in'));
        this.images.forEach(image => image.classList.add('animate-in'));
        return;
      }

      const options = {
        threshold: [0, 0.15, 0.5, 1], // Multiple thresholds for better detection
        rootMargin: '-80px 0px -80px 0px' // Trigger slightly inside viewport
      };

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const target = entry.target;
          
          if (entry.isIntersecting) {
            // Element is entering or in viewport - animate in
            target.classList.remove('animate-out');
            target.classList.add('animate-in');
          } else {
            // Element is leaving viewport - animate out
            // Only animate out if it was previously animated in
            if (target.classList.contains('animate-in')) {
              target.classList.remove('animate-in');
              target.classList.add('animate-out');
            }
          }
        });
      }, options);

      // Observe steps
      this.steps.forEach(step => this.intersectionObserver.observe(step));
      
      // Observe images separately for delayed animation
      this.images.forEach(image => this.intersectionObserver.observe(image));
    }

    setupScrollProgress() {
      if (!this.progressBar || !this.progressFill) return;

      // Start listening when section is near viewport
      if ('IntersectionObserver' in window) {
        const progressObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              window.addEventListener('scroll', this.handleScroll, { passive: true });
              window.addEventListener('resize', this.handleResize, { passive: true });
              this.updateProgress();
            } else {
              window.removeEventListener('scroll', this.handleScroll);
              window.removeEventListener('resize', this.handleResize);
            }
          });
        }, {
          rootMargin: '200px 0px'
        });

        progressObserver.observe(this.section);
        this.progressObserver = progressObserver;
      } else {
        // Fallback: always listen
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });
        this.updateProgress();
      }
    }

    handleScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.updateProgress();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    handleResize() {
      this.handleScroll();
    }

    updateProgress() {
      const rect = this.section.getBoundingClientRect();
      const sectionHeight = this.section.offsetHeight;
      const windowHeight = window.innerHeight;
      
      // Calculate scroll progress through the section
      const scrollStart = -rect.top;
      const scrollEnd = sectionHeight - windowHeight;
      
      let progress = 0;
      if (scrollEnd > 0) {
        progress = scrollStart / scrollEnd;
        progress = Math.max(0, Math.min(1, progress));
      }
      
      this.progressFill.style.transform = `scaleY(${progress})`;
    }

    destroy() {
      // Clean up event listeners
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      
      // Clean up observers
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      if (this.progressObserver) {
        this.progressObserver.disconnect();
      }
    }
  }

  let animators = [];

  function initProcessSteps() {
    // Clean up existing animators
    animators.forEach(animator => animator.destroy());
    animators = [];

    const sections = document.querySelectorAll('.salonid-process-steps');
    
    if (sections.length === 0) {
      return;
    }

    sections.forEach(section => {
      const animator = new ProcessStepsAnimator(section);
      animators.push(animator);
    });
    
    console.log('✅ SalonID Process Steps initialized -', animators.length, 'sections');
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProcessSteps);
  } else {
    initProcessSteps();
  }

  // Re-initialize on Shopify theme editor updates
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(initProcessSteps, 100);
    });
    document.addEventListener('shopify:section:reorder', () => {
      setTimeout(initProcessSteps, 100);
    });
  }

  // Cleanup on unload
  window.addEventListener('beforeunload', () => {
    animators.forEach(animator => animator.destroy());
  });

  // Export for potential external use
  window.SalonIDProcessSteps = ProcessStepsAnimator;
})();